import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, ToggleButtonGroup, ToggleButton, Typography,
  InputAdornment, Chip, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Search, LocationOn, LocationCity, QrCode2, Sync as SyncIcon
} from '@mui/icons-material';
import PlantCard from '../components/PlantCard';
import useGps from '../hooks/useGps';
import useOfflineSync from '../hooks/useOfflineSync';
import pwaApi from '../services/pwaApi';
import * as offlineDb from '../services/offlineDb';

const SORT_OPTIONS = [
  { value: 'distance', label: 'Distanza', icon: <LocationOn fontSize="small" /> },
  { value: 'plant_code', label: 'Codice', icon: <QrCode2 fontSize="small" /> },
  { value: 'citta', label: 'Città', icon: <LocationCity fontSize="small" /> },
];

export default function PlantListPage() {
  const navigate = useNavigate();
  const { position } = useGps();
  const { isOnline, pendingCount, isSyncing, syncNow } = useOfflineSync();

  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('distance');
  const [provincia, setProvincia] = useState(() => localStorage.getItem('pwa_provincia_filter') || '');
  const [provinces, setProvinces] = useState([]);

  // Carica lista province
  useEffect(() => {
    if (isOnline) {
      pwaApi.getProvinces().then(res => {
        setProvinces(res.data || []);
      }).catch(() => {});
    } else {
      // Offline: estrai province dai dati cachati
      offlineDb.getPlants().then(cached => {
        if (cached?.length) {
          const provs = [...new Set(cached.map(p => p.provincia).filter(Boolean))].sort();
          setProvinces(provs);
        }
      }).catch(() => {});
    }
  }, [isOnline]);

  const handleProvinciaChange = (value) => {
    setProvincia(value);
    if (value) {
      localStorage.setItem('pwa_provincia_filter', value);
    } else {
      localStorage.removeItem('pwa_provincia_filter');
    }
  };

  const loadPlants = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (isOnline) {
        const params = {};
        if (position) {
          params.lat = position.latitude;
          params.lng = position.longitude;
        }
        if (search.trim()) params.search = search.trim();
        if (provincia) params.provincia = provincia;
        params.sortBy = sortBy;
        const res = await pwaApi.getPlants(params);
        setPlants(res.data || []);
      } else {
        const cached = await offlineDb.getPlants();
        setPlants(cached || []);
      }
    } catch (err) {
      setError('Errore nel caricamento impianti');
      // fallback to offline
      try {
        const cached = await offlineDb.getPlants();
        if (cached?.length) setPlants(cached);
      } catch (_) {}
    } finally {
      setLoading(false);
    }
  }, [isOnline, position, search, sortBy, provincia]);

  useEffect(() => {
    const timer = setTimeout(loadPlants, 300);
    return () => clearTimeout(timer);
  }, [loadPlants]);

  const sortedPlants = useMemo(() => {
    let sorted = [...plants];

    // Filtro offline per provincia (case-insensitive)
    if (!isOnline && provincia) {
      const provUpper = provincia.toUpperCase();
      sorted = sorted.filter(p => (p.provincia || '').toUpperCase() === provUpper);
    }

    if (!isOnline && search.trim()) {
      const q = search.toLowerCase();
      sorted = sorted.filter(p =>
        (p.nome || '').toLowerCase().includes(q) ||
        (p.citta || '').toLowerCase().includes(q) ||
        (p.provincia || '').toLowerCase().includes(q) ||
        (p.indirizzo || '').toLowerCase().includes(q) ||
        (p.plant_code || '').toLowerCase().includes(q)
      );
    }

    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'distance': {
          if (!position) return (a.plant_code || '').localeCompare(b.plant_code || '');
          const dA = haversine(position.latitude, position.longitude,
            a.latitude || a.gps_lat, a.longitude || a.gps_lng);
          const dB = haversine(position.latitude, position.longitude,
            b.latitude || b.gps_lat, b.longitude || b.gps_lng);
          return dA - dB;
        }
        case 'plant_code':
          return (a.plant_code || '').localeCompare(b.plant_code || '');
        case 'citta':
          return (a.citta || '').localeCompare(b.citta || '');
        default:
          return 0;
      }
    });

    return sorted;
  }, [plants, search, sortBy, isOnline, position, provincia]);

  const handlePlantClick = (plant) => {
    navigate(`/letture/plant/${plant.id}`);
  };

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Cerca impianto, città, provincia..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search color="action" />
            </InputAdornment>
          )
        }}
        sx={{ mb: 1.5 }}
      />

      {/* Filtro provincia */}
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel>Provincia</InputLabel>
        <Select
          value={provincia}
          label="Provincia"
          onChange={(e) => handleProvinciaChange(e.target.value)}
        >
          <MenuItem value="">Tutte le province</MenuItem>
          {provinces.map(p => (
            <MenuItem key={p} value={p}>{p}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Sort */}
      <ToggleButtonGroup
        value={sortBy}
        exclusive
        onChange={(e, v) => v && setSortBy(v)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      >
        {SORT_OPTIONS.map(opt => (
          <ToggleButton key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem', py: 0.5 }}>
            {opt.icon}
            <Box component="span" sx={{ ml: 0.5, display: { xs: 'none', sm: 'inline' } }}>
              {opt.label}
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Status chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={`${sortedPlants.length} impianti`}
          color="primary"
          variant="outlined"
        />
        {pendingCount > 0 && (
          <Chip
            size="small"
            icon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
            label={isSyncing ? 'Sincronizzazione...' : `${pendingCount} da sincronizzare`}
            color="warning"
            onClick={isOnline && !isSyncing ? syncNow : undefined}
            sx={isOnline && !isSyncing ? { cursor: 'pointer' } : {}}
          />
        )}
        {position && sortBy === 'distance' && (
          <Chip size="small" icon={<LocationOn />} label="GPS attivo" color="success" variant="outlined" />
        )}
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Caricamento impianti...
          </Typography>
        </Box>
      ) : sortedPlants.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {search ? 'Nessun impianto trovato' : 'Nessun impianto assegnato'}
          </Typography>
        </Box>
      ) : (
        sortedPlants.map(plant => (
          <PlantCard
            key={plant.id}
            plant={plant}
            position={position}
            onClick={() => handlePlantClick(plant)}
          />
        ))
      )}
    </Box>
  );
}

function haversine(lat1, lon1, lat2, lon2) {
  if (!lat2 || !lon2) return Infinity;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
